import { Button, Flex, Image, Tag, message } from "antd";
import { useTranslation } from "react-i18next";

import Paragraph from "antd/es/typography/Paragraph";
import Title from "antd/es/typography/Title";
import copyIcon from "../../../../assets/icons/copy.svg";
import CustomCard from "../../../../components/CustomCard";
import { CouponOutline } from "../../../../constants/iconExports";
import { couponType } from "../../../../types/coupon";
import { getDateTime } from "../../../../utils/getDate";
import { isDarkMode } from "../../../../utils/useColor";
import React from "react";

type CouponListProps = {
  shopURL: string;
  data: couponType[];
  unusable?: boolean;
  backgroundColor?: string;
};

const CouponList: React.FC<CouponListProps> = (props: CouponListProps) => {
  const { shopURL, data, unusable = false, backgroundColor } = props;
  const isDark = isDarkMode(backgroundColor ?? "");
  const { t } = useTranslation();

  const copyToClipboard = (text: string) => {
    navigator.clipboard.writeText(text);
    message.success(t("Copied to clipboard"));
  };

  if (data.length === 0) {
    return (
      <Flex vertical align="center" justify="flex-end" className="h-[284px]">
        <CouponOutline
          color={isDark ? "#fff" : "var(--app-foreground-color)"}
        />
        <Paragraph
          className="h3-bold"
          style={{ color: isDark ? "#fff" : "var(--app-foreground-color)" }}
        >
          {t("No coupon found")}
        </Paragraph>
      </Flex>
    );
  }

  const getExpirationDate = (coupon: couponType): string => {
    if (coupon.start_date === null && coupon.end_date === null) {
      return t("Unlimited");
    } else if (coupon.start_date === null && coupon.end_date !== null) {
      return `- ${getDateTime(coupon.end_date)}`;
    } else if (coupon.start_date !== null && coupon.end_date === null) {
      return `${getDateTime(coupon.start_date)} - ${t("Unlimited")}`;
    } else if (coupon.start_date !== null && coupon.end_date !== null) {
      return `${getDateTime(coupon.start_date)} - ${getDateTime(coupon.end_date)}`;
    }
    return "";
  };

  return (
    <Flex vertical gap={"18px"} className="p-4 w-full">
      {data.map((coupon, index) => (
        <CustomCard shadow={false} key={`${coupon.code}${index}`}>
          <Flex
            vertical
            style={{ padding: "28px 16px 28px 16px" }}
            gap={"16px"}
          >
            <Flex vertical gap={"8px"}>
              <Tag
                style={{
                  padding: "2px 8px 2px 8px",
                  color: unusable ? "#425A70" : "var(--app-foreground-color)",
                  backgroundColor: unusable
                    ? "#E7EBEF"
                    : "var(--app-lighten-foreground-color)",
                  border: "none",
                }}
                className="w-fit rounded-[20px] note-medium"
              >
                {t(coupon.status)}
              </Tag>

              <Flex vertical gap={"4px"}>
                <Title level={2} className="h3-bold text-o-body">
                  {coupon.name || "-"}
                </Title>

                <Paragraph className="note-regular text-o-description">
                  {getExpirationDate(coupon)}
                </Paragraph>
                <Paragraph className="note-regular text-o-description">
                  {coupon.description || "-"}
                </Paragraph>
              </Flex>
            </Flex>
            <Flex
              gap={"4px"}
              onClick={() => copyToClipboard(coupon.code)}
              align="center"
            >
              <Paragraph className="note-regular text-o-body">
                {t("Redemption Code")} {coupon.code}
              </Paragraph>
              <Image src={copyIcon} preview={false} />
            </Flex>
            {!unusable && (
              <Button
                style={{
                  padding: "8px 12px 8px 12px",
                  backgroundColor: "var(--app-foreground-color)",
                }}
                className="h-[36px] w-full rounded-[4px] text-white body-regular"
                type="primary"
                href={shopURL}
              >
                {t("Use Coupon")}
              </Button>
            )}
          </Flex>
        </CustomCard>
      ))}
      {data.length > 0 && (
        <Flex justify="center">
          <Button
            style={{
              padding: "8px 12px 8px 12px",
              color: "var(--app-foreground-color)",
              borderColor: "var(--app-foreground-color)",
            }}
            className="h-[36px] w-[136px] rounded-[4px]"
            ghost
            type="primary"
            href={shopURL}
          >
            {t("Load more coupons")}
          </Button>
        </Flex>
      )}
    </Flex>
  );
};
export default CouponList;
